import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Search, Plus, Edit, Trash2, Eye, ChevronUp, ChevronDown, Loader2, AlertCircle, MessageSquareText, AlertTriangle, Check, Globe, Settings } from 'lucide-react';
import {
  apiService,
  ContactReason,
  PromptTemplate,
  CreateContactReasonRequest,
  UpdateContactReasonRequest,
  LanguageConfigResponse
} from '../../services/api';
import { toast } from 'sonner';
import { LanguageSettingsDialog } from './LanguageSettingsDialog';

// Form data interface including i18n fields
interface ContactReasonFormData {
  templateId: string;
  name: string;
  description: string;
  promptSnippet: string;
  nameI18n: Record<string, string>;
  descriptionI18n: Record<string, string>;
  promptSnippetI18n: Record<string, string>;
  sortOrder: number;
  isActive: boolean;
}

export function ContactReasonsPage() {
  // Data state
  const [reasons, setReasons] = useState<ContactReason[]>([]);
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Language configuration
  const [languageConfig, setLanguageConfig] = useState<LanguageConfigResponse | null>(null);
  const [activeLanguageTab, setActiveLanguageTab] = useState<string>('de');

  // Filters
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewingReason, setViewingReason] = useState<ContactReason | null>(null);
  const [editingReason, setEditingReason] = useState<ContactReason | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLanguageSettingsOpen, setIsLanguageSettingsOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState<ContactReasonFormData>({
    templateId: '',
    name: '',
    description: '',
    promptSnippet: '',
    nameI18n: { de: '' },
    descriptionI18n: { de: '' },
    promptSnippetI18n: { de: '' },
    sortOrder: 0,
    isActive: true
  });

  // Load templates and language config on mount
  useEffect(() => {
    loadTemplates();
    loadLanguageConfig();
  }, []);

  const loadLanguageConfig = async () => {
    try {
      const config = await apiService.getLanguageConfig();
      setLanguageConfig(config);
      // Set first active language as default tab
      if (config.activeLanguages && config.activeLanguages.length > 0) {
        setActiveLanguageTab(config.activeLanguages[0].code);
      }
    } catch (err) {
      console.error('Failed to load language config:', err);
    }
  };

  // Callback when language config changes
  const handleLanguageConfigChange = () => {
    loadLanguageConfig();
    if (selectedTemplateId) {
      loadReasons(); // Reload to get updated missingLanguages
    }
  };

  // Get active languages from config
  const getActiveLanguages = (): { code: string; label: string }[] => {
    if (!languageConfig?.activeLanguages) return [{ code: 'de', label: 'Deutsch' }];
    return languageConfig.activeLanguages;
  };

  // Check if a contact reason has missing translations
  const hasMissingTranslations = (reason: ContactReason): boolean => {
    return (reason.missingLanguages?.length ?? 0) > 0;
  };

  // Get missing languages for a contact reason
  const getMissingLanguages = (reason: ContactReason): string[] => {
    return reason.missingLanguages || [];
  };

  // Load contact reasons when template selection or filters change
  useEffect(() => {
    if (selectedTemplateId) {
      loadReasons();
    } else {
      setReasons([]);
      setIsLoading(false);
    }
  }, [selectedTemplateId, includeInactive]);

  const loadTemplates = async () => {
    try {
      setIsLoadingTemplates(true);
      const response = await apiService.getPromptTemplates({ limit: 100 });
      setTemplates(response.templates);
    } catch (err: any) {
      toast.error('Templates konnten nicht geladen werden');
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const loadReasons = async () => {
    if (!selectedTemplateId) return;

    try {
      setIsLoading(true);
      setError(null);
      const data = await apiService.getContactReasonsByTemplate(selectedTemplateId, includeInactive);
      setReasons(data);
    } catch (err: any) {
      setError(err.message || 'Kontaktgründe konnten nicht geladen werden');
      toast.error('Kontaktgründe konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = (reason: ContactReason) => {
    setViewingReason(reason);
    setIsDialogOpen(true);
  };

  const handleEdit = (reason: ContactReason) => {
    setEditingReason(reason);

    // Initialize i18n fields from existing data
    const nameI18n: Record<string, string> = reason.nameI18n || {};
    const descriptionI18n: Record<string, string> = reason.descriptionI18n || {};
    const promptSnippetI18n: Record<string, string> = reason.promptSnippetI18n || {};

    // If no i18n data, use legacy fields for default language
    if (Object.keys(nameI18n).length === 0 && reason.name) {
      nameI18n.de = reason.name;
    }
    if (Object.keys(descriptionI18n).length === 0 && reason.description) {
      descriptionI18n.de = reason.description;
    }
    if (Object.keys(promptSnippetI18n).length === 0 && reason.promptSnippet) {
      promptSnippetI18n.de = reason.promptSnippet;
    }

    setFormData({
      templateId: reason.templateId,
      name: reason.name,
      description: reason.description || '',
      promptSnippet: reason.promptSnippet,
      nameI18n,
      descriptionI18n,
      promptSnippetI18n,
      sortOrder: reason.sortOrder,
      isActive: reason.isActive
    });
    setViewingReason(null);
    setActiveLanguageTab(getActiveLanguages()[0]?.code || 'de');
    setIsDialogOpen(true);
  };

  const handleCreate = () => {
    if (!selectedTemplateId) {
      toast.error('Bitte wählen Sie zuerst ein Template aus');
      return;
    }
    setEditingReason(null);
    setViewingReason(null);
    setFormData({
      templateId: selectedTemplateId,
      name: '',
      description: '',
      promptSnippet: '',
      nameI18n: { de: '' },
      descriptionI18n: { de: '' },
      promptSnippetI18n: { de: '' },
      sortOrder: reasons.length, // New item at the end
      isActive: true
    });
    setActiveLanguageTab(getActiveLanguages()[0]?.code || 'de');
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    // Validate that at least one language has name and prompt snippet
    const hasContent = Object.values(formData.nameI18n).some(content => content?.trim()) &&
                       Object.values(formData.promptSnippetI18n).some(content => content?.trim());

    if (!hasContent) {
      toast.error('Name und Prompt-Text sind in mindestens einer Sprache erforderlich');
      return;
    }

    try {
      setIsSaving(true);

      // Clean up empty i18n entries
      const cleanedNameI18n: Record<string, string> = {};
      const cleanedDescriptionI18n: Record<string, string> = {};
      const cleanedPromptSnippetI18n: Record<string, string> = {};

      for (const [lang, content] of Object.entries(formData.nameI18n)) {
        if (content?.trim()) {
          cleanedNameI18n[lang] = content.trim();
        }
      }
      for (const [lang, content] of Object.entries(formData.descriptionI18n)) {
        if (content?.trim()) {
          cleanedDescriptionI18n[lang] = content.trim();
        }
      }
      for (const [lang, content] of Object.entries(formData.promptSnippetI18n)) {
        if (content?.trim()) {
          cleanedPromptSnippetI18n[lang] = content.trim();
        }
      }

      // Use first available name and prompt snippet as legacy fields
      const firstLang = Object.keys(cleanedNameI18n)[0] || 'de';
      const legacyName = cleanedNameI18n[firstLang] || '';
      const legacyDescription = cleanedDescriptionI18n[firstLang] || '';
      const legacyPromptSnippet = cleanedPromptSnippetI18n[firstLang] || '';

      if (editingReason) {
        const updateData: UpdateContactReasonRequest = {
          name: legacyName,
          description: legacyDescription,
          promptSnippet: legacyPromptSnippet,
          nameI18n: cleanedNameI18n,
          descriptionI18n: Object.keys(cleanedDescriptionI18n).length > 0 ? cleanedDescriptionI18n : undefined,
          promptSnippetI18n: cleanedPromptSnippetI18n,
          isActive: formData.isActive
        };
        await apiService.updateContactReason(editingReason.id, updateData);
        toast.success('Kontaktgrund aktualisiert');
      } else {
        const createData: CreateContactReasonRequest = {
          templateId: formData.templateId,
          name: legacyName,
          description: legacyDescription,
          promptSnippet: legacyPromptSnippet,
          nameI18n: cleanedNameI18n,
          descriptionI18n: Object.keys(cleanedDescriptionI18n).length > 0 ? cleanedDescriptionI18n : undefined,
          promptSnippetI18n: cleanedPromptSnippetI18n,
          sortOrder: formData.sortOrder,
          isActive: formData.isActive
        };
        await apiService.createContactReason(createData);
        toast.success('Kontaktgrund erstellt');
      }

      closeDialog();
      await loadReasons();
    } catch (err: any) {
      toast.error(err.message || 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Kontaktgrund wirklich löschen?')) return;

    try {
      await apiService.deleteContactReason(id);
      toast.success('Kontaktgrund gelöscht');
      await loadReasons();
    } catch (err: any) {
      toast.error(err.message || 'Fehler beim Löschen');
    }
  };

  const toggleActive = async (reason: ContactReason) => {
    try {
      await apiService.toggleContactReasonActive(reason.id);
      toast.success(reason.isActive ? 'Kontaktgrund deaktiviert' : 'Kontaktgrund aktiviert');
      await loadReasons();
    } catch (err: any) {
      toast.error(err.message || 'Fehler beim Ändern des Status');
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    await reorderReasons(index, index - 1);
  };

  const handleMoveDown = async (index: number) => {
    if (index === reasons.length - 1) return;
    await reorderReasons(index, index + 1);
  };

  const reorderReasons = async (fromIndex: number, toIndex: number) => {
    const newReasons = [...reasons];
    const [removed] = newReasons.splice(fromIndex, 1);
    newReasons.splice(toIndex, 0, removed);

    // Optimistically update UI
    setReasons(newReasons);

    try {
      const orderedIds = newReasons.map(r => r.id);
      await apiService.reorderContactReasons(selectedTemplateId, orderedIds);
      toast.success('Reihenfolge aktualisiert');
    } catch (err: any) {
      toast.error('Fehler beim Neuordnen');
      await loadReasons(); // Revert to server state
    }
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingReason(null);
    setViewingReason(null);
    setFormData({
      templateId: selectedTemplateId,
      name: '',
      description: '',
      promptSnippet: '',
      nameI18n: { de: '' },
      descriptionI18n: { de: '' },
      promptSnippetI18n: { de: '' },
      sortOrder: 0,
      isActive: true
    });
    setActiveLanguageTab(getActiveLanguages()[0]?.code || 'de');
  };

  const filteredReasons = reasons.filter(reason =>
    reason.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (reason.description?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getTemplateName = (templateId: string): string => {
    const template = templates.find(t => t.id === templateId);
    return template?.name || 'Unbekanntes Template';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kontaktgründe</h1>
          <p className="text-gray-600">Verwaltung von Kontaktgründen für Prompt Templates</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setIsLanguageSettingsOpen(true)}
            disabled={isLoading}
          >
            <Settings className="w-4 h-4 mr-2" />
            Sprachen
          </Button>
          <Button onClick={handleCreate} disabled={!selectedTemplateId || isLoading}>
            <Plus className="w-4 h-4 mr-2" />
            Neuer Kontaktgrund
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <div className="w-64">
          <Select
            value={selectedTemplateId}
            onValueChange={setSelectedTemplateId}
            disabled={isLoadingTemplates}
          >
            <SelectTrigger>
              <SelectValue placeholder="Template auswählen..." />
            </SelectTrigger>
            <SelectContent>
              {templates.map((template) => (
                <SelectItem key={template.id} value={template.id}>
                  {template.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            disabled={!selectedTemplateId || isLoading}
          />
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="includeInactive"
            checked={includeInactive}
            onCheckedChange={(checked) => setIncludeInactive(checked === true)}
            disabled={!selectedTemplateId}
          />
          <Label htmlFor="includeInactive" className="text-sm text-gray-600">
            Inaktive anzeigen
          </Label>
        </div>
      </div>

      {/* Content */}
      {!selectedTemplateId ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquareText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Bitte wählen Sie ein Template aus</p>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Lade Kontaktgründe...</span>
        </div>
      ) : error ? (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={loadReasons}>Erneut versuchen</Button>
          </CardContent>
        </Card>
      ) : filteredReasons.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500 mb-4">
              {searchTerm
                ? 'Keine Kontaktgründe gefunden'
                : 'Keine Kontaktgründe für dieses Template'}
            </p>
            {!searchTerm && (
              <Button onClick={handleCreate}>
                Ersten Kontaktgrund erstellen
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredReasons.map((reason, index) => (
            <Card
              key={reason.id}
              className={`hover:shadow-md transition-shadow ${!reason.isActive ? 'opacity-60' : ''}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-xs">
                        #{index + 1}
                      </Badge>
                      {!reason.isActive && (
                        <Badge variant="secondary" className="text-xs">
                          Inaktiv
                        </Badge>
                      )}
                      {hasMissingTranslations(reason) && (
                        <Badge variant="outline" className="text-xs text-amber-600 border-amber-300 bg-amber-50">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {getMissingLanguages(reason).length} fehlend
                        </Badge>
                      )}
                      {reason.isFullyTranslated && (
                        <Badge variant="outline" className="text-xs text-green-600 border-green-300 bg-green-50">
                          <Check className="w-3 h-3 mr-1" />
                          Vollständig
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-lg">{reason.name}</CardTitle>
                    <CardDescription className="mt-1">
                      {reason.description || 'Keine Beschreibung'}
                    </CardDescription>
                  </div>
                  <Switch
                    checked={reason.isActive}
                    onCheckedChange={() => toggleActive(reason)}
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 max-h-24 overflow-y-auto">
                    {reason.promptSnippet.substring(0, 150)}
                    {reason.promptSnippet.length > 150 && '...'}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      Erstellt: {new Date(reason.createdAt).toLocaleDateString('de-DE')}
                    </span>
                    <div className="flex items-center space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                        title="Nach oben"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveDown(index)}
                        disabled={index === filteredReasons.length - 1}
                        title="Nach unten"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleView(reason)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(reason)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(reason.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {viewingReason
                ? 'Kontaktgrund Details'
                : editingReason
                ? 'Kontaktgrund bearbeiten'
                : 'Neuer Kontaktgrund'}
            </DialogTitle>
            <DialogDescription>
              {viewingReason
                ? 'Detaillierte Ansicht des Kontaktgrunds'
                : 'Erstellen oder bearbeiten Sie einen Kontaktgrund für das ausgewählte Template'}
            </DialogDescription>
          </DialogHeader>

          {viewingReason ? (
            // View Mode
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Informationen</h4>
                    <div className="space-y-2 text-sm">
                      <div><strong>Name:</strong> {viewingReason.name}</div>
                      <div><strong>Template:</strong> {getTemplateName(viewingReason.templateId)}</div>
                      <div><strong>Status:</strong> {viewingReason.isActive ? 'Aktiv' : 'Inaktiv'}</div>
                      <div><strong>Reihenfolge:</strong> {viewingReason.sortOrder + 1}</div>
                      <div><strong>Erstellt:</strong> {new Date(viewingReason.createdAt).toLocaleDateString('de-DE')}</div>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Übersetzungsstatus</h4>
                    <div className="flex flex-wrap gap-2">
                      {viewingReason.availableLanguages?.map((lang) => (
                        <Badge key={lang} variant="outline" className="text-xs text-green-600 border-green-300 bg-green-50">
                          <Check className="w-3 h-3 mr-1" />
                          {lang.toUpperCase()}
                        </Badge>
                      ))}
                      {viewingReason.missingLanguages?.map((lang) => (
                        <Badge key={lang} variant="outline" className="text-xs text-amber-600 border-amber-300 bg-amber-50">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {lang.toUpperCase()}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Beschreibung</h4>
                    <p className="text-sm text-gray-600">
                      {viewingReason.description || 'Keine Beschreibung vorhanden'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Translations View */}
              <Tabs defaultValue={getActiveLanguages()[0]?.code || 'de'}>
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium">Übersetzungen</span>
                </div>
                <TabsList>
                  {getActiveLanguages().map((lang) => {
                    const hasTranslation = viewingReason.nameI18n?.[lang.code] &&
                                          viewingReason.promptSnippetI18n?.[lang.code];
                    return (
                      <TabsTrigger key={lang.code} value={lang.code}>
                        {lang.label}
                        <span
                          className={`w-2 h-2 ml-1.5 rounded-full ${
                            hasTranslation ? 'bg-green-500' : 'bg-red-500'
                          }`}
                        />
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
                {getActiveLanguages().map((lang) => (
                  <TabsContent key={lang.code} value={lang.code} className="space-y-4 mt-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Name ({lang.label})</h4>
                      <p className="text-sm text-gray-800">
                        {viewingReason.nameI18n?.[lang.code] || (
                          <span className="text-gray-400 italic">Nicht übersetzt</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Beschreibung ({lang.label})</h4>
                      <p className="text-sm text-gray-600">
                        {viewingReason.descriptionI18n?.[lang.code] || (
                          <span className="text-gray-400 italic">Nicht übersetzt</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Prompt-Text ({lang.label})</h4>
                      <div className="bg-gray-50 rounded-lg p-4 max-h-48 overflow-y-auto">
                        {viewingReason.promptSnippetI18n?.[lang.code] ? (
                          <pre className="whitespace-pre-wrap text-sm text-gray-800">
                            {viewingReason.promptSnippetI18n[lang.code]}
                          </pre>
                        ) : (
                          <span className="text-gray-400 italic">Nicht übersetzt</span>
                        )}
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={closeDialog}>
                  Schließen
                </Button>
                <Button onClick={() => handleEdit(viewingReason)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Bearbeiten
                </Button>
              </div>
            </div>
          ) : (
            // Edit/Create Mode
            <div className="space-y-4">
              {/* Language Tabs for i18n content */}
              <Tabs value={activeLanguageTab} onValueChange={setActiveLanguageTab}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium">Sprache</span>
                  </div>
                  <TabsList>
                    {getActiveLanguages().map((lang) => {
                      const hasContent = formData.nameI18n[lang.code]?.trim() &&
                                        formData.promptSnippetI18n[lang.code]?.trim();
                      return (
                        <TabsTrigger
                          key={lang.code}
                          value={lang.code}
                          className="relative"
                        >
                          {lang.label}
                          <span
                            className={`w-2 h-2 ml-1.5 rounded-full ${
                              hasContent ? 'bg-green-500' : 'bg-red-500'
                            }`}
                          />
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                </div>

                {getActiveLanguages().map((lang) => (
                  <TabsContent key={lang.code} value={lang.code} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor={`name-${lang.code}`}>Name ({lang.label}) *</Label>
                      <Input
                        id={`name-${lang.code}`}
                        value={formData.nameI18n[lang.code] || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          nameI18n: { ...formData.nameI18n, [lang.code]: e.target.value }
                        })}
                        placeholder="z.B. Technischer Support"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`description-${lang.code}`}>Beschreibung ({lang.label})</Label>
                      <Textarea
                        id={`description-${lang.code}`}
                        value={formData.descriptionI18n[lang.code] || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          descriptionI18n: { ...formData.descriptionI18n, [lang.code]: e.target.value }
                        })}
                        placeholder="Optionale Beschreibung für die Verwaltung..."
                        rows={2}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`promptSnippet-${lang.code}`}>Prompt-Text ({lang.label}) *</Label>
                      <Textarea
                        id={`promptSnippet-${lang.code}`}
                        value={formData.promptSnippetI18n[lang.code] || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          promptSnippetI18n: { ...formData.promptSnippetI18n, [lang.code]: e.target.value }
                        })}
                        placeholder="Anweisungen für den Bot, wenn dieser Kontaktgrund ausgewählt wird..."
                        className="min-h-32"
                      />
                      <p className="text-xs text-gray-500">
                        Dieser Text wird in den System-Prompt des Agenten eingefügt, wenn der Kontaktgrund ausgewählt ist.
                      </p>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>

              <div className="flex items-center space-x-2 pt-2 border-t">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
                <Label htmlFor="isActive">Kontaktgrund aktiv</Label>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={closeDialog} disabled={isSaving}>
                  Abbrechen
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Speichern...
                    </>
                  ) : (
                    editingReason ? 'Speichern' : 'Erstellen'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Language Settings Dialog */}
      <LanguageSettingsDialog
        open={isLanguageSettingsOpen}
        onOpenChange={setIsLanguageSettingsOpen}
        onConfigChange={handleLanguageConfigChange}
      />
    </div>
  );
}
